import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const STORAGE_KEY = 'yolal-dashboard-config'
const CONFIG_VERSION = '1.0'

// Default configuration for quick actions
const defaultQuickActions = [
  { id: 'addBooking', visible: true },
  { id: 'adminPurchase', visible: true },
  { id: 'setUnavailable', visible: true },
  { id: 'customers', visible: true },
  { id: 'teachers', visible: true }
]

// Default configuration for statistics cards
const defaultStatistics = [
  { key: 'todayBookings', visible: true },
  { key: 'vipRevenue', visible: true },
  { key: 'monthlyIncome', visible: true },
  { key: 'customers', visible: true },
  { key: 'teachers', visible: true },
  { key: 'horses', visible: true },
  { key: 'vipPackages', visible: true }
]

export const useDashboardCustomizationStore = defineStore('dashboardCustomization', () => {
  // State
  const quickActionsConfig = ref([...defaultQuickActions])
  const statisticsConfig = ref([...defaultStatistics])
  const isCustomizing = ref(false)

  // Computed
  const visibleQuickActions = computed(() => {
    return quickActionsConfig.value
      .filter(action => action.visible)
      .map(action => action.id)
  })

  const visibleStatistics = computed(() => {
    return statisticsConfig.value
      .filter(stat => stat.visible)
      .map(stat => stat.key)
  })

  // Actions
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const config = JSON.parse(stored)
        
        // Version check for future compatibility
        if (config.version === CONFIG_VERSION) {
          if (config.quickActions && Array.isArray(config.quickActions)) {
            quickActionsConfig.value = config.quickActions
          }
          if (config.statistics && Array.isArray(config.statistics)) {
            statisticsConfig.value = config.statistics
          }
        }
      }
    } catch (error) {
      console.error('Failed to load dashboard customization from storage:', error)
      // Reset to defaults on error
      resetToDefault()
    }
  }

  const saveToStorage = () => {
    try {
      const config = {
        version: CONFIG_VERSION,
        quickActions: quickActionsConfig.value,
        statistics: statisticsConfig.value
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
      return true
    } catch (error) {
      console.error('Failed to save dashboard customization to storage:', error)
      return false
    }
  }

  const toggleQuickAction = (actionId) => {
    const action = quickActionsConfig.value.find(a => a.id === actionId)
    if (action) {
      action.visible = !action.visible
      saveToStorage()
    }
  }

  const toggleStatistic = (statKey) => {
    const stat = statisticsConfig.value.find(s => s.key === statKey)
    if (stat) {
      stat.visible = !stat.visible
      saveToStorage()
    }
  }

  const reorderQuickActions = (newOrder) => {
    // newOrder should be an array of action IDs in the desired order
    const reordered = []
    newOrder.forEach(id => {
      const action = quickActionsConfig.value.find(a => a.id === id)
      if (action) {
        reordered.push(action)
      }
    })
    
    // Add any missing actions at the end
    quickActionsConfig.value.forEach(action => {
      if (!reordered.find(a => a.id === action.id)) {
        reordered.push(action)
      }
    })
    
    quickActionsConfig.value = reordered
    saveToStorage()
  }

  const reorderStatistics = (newOrder) => {
    // newOrder should be an array of stat keys in the desired order
    const reordered = []
    newOrder.forEach(key => {
      const stat = statisticsConfig.value.find(s => s.key === key)
      if (stat) {
        reordered.push(stat)
      }
    })
    
    // Add any missing stats at the end
    statisticsConfig.value.forEach(stat => {
      if (!reordered.find(s => s.key === stat.key)) {
        reordered.push(stat)
      }
    })
    
    statisticsConfig.value = reordered
    saveToStorage()
  }

  const resetToDefault = () => {
    quickActionsConfig.value = [...defaultQuickActions]
    statisticsConfig.value = [...defaultStatistics]
    saveToStorage()
  }

  const enterCustomizeMode = () => {
    isCustomizing.value = true
  }

  const exitCustomizeMode = () => {
    isCustomizing.value = false
  }

  // Initialize - load from storage on store creation
  loadFromStorage()

  return {
    // State
    quickActionsConfig,
    statisticsConfig,
    isCustomizing,
    
    // Computed
    visibleQuickActions,
    visibleStatistics,
    
    // Actions
    loadFromStorage,
    saveToStorage,
    toggleQuickAction,
    toggleStatistic,
    reorderQuickActions,
    reorderStatistics,
    resetToDefault,
    enterCustomizeMode,
    exitCustomizeMode
  }
})
